import React, { useState, useRef } from "react";
import { Box, Typography, IconButton, Stack, Alert, LinearProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { 
  Paperclip, 
  Image as ImageIcon, 
  Video, 
  FileText,
  X,
  Download,
  Eye
} from "lucide-react";
import { formatFileSize } from '../utils/fileSize';

// Constantes para validación de archivos
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov'];
const ACCEPTED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv'
];

// Helper para formatear el tamaño de archivo
// Helper para obtener el tipo de archivo
const getFileType = (file) => {
  if (ACCEPTED_IMAGE_TYPES.includes(file.type)) return 'image';
  if (ACCEPTED_VIDEO_TYPES.includes(file.type)) return 'video';
  if (ACCEPTED_DOCUMENT_TYPES.includes(file.type)) return 'document';
  return 'other';
};

export const validateAttachmentFile = (file) => {
  if (file.size > MAX_FILE_SIZE) {
    return 'El archivo es demasiado grande. El tamaño máximo permitido es 50MB.';
  }
  if (getFileType(file) === 'other') {
    return 'Tipo de archivo no soportado. Solo se permiten imágenes, videos y documentos.';
  }
  return null;
};

// Helper para obtener el icono según el tipo
const getFileIcon = (type) => {
  switch (type) {
    case 'image': return <ImageIcon size={20} />;
    case 'video': return <Video size={20} />;
    case 'document': return <FileText size={20} />;
    default: return <Paperclip size={20} />;
  }
};

// Componente para mostrar archivo adjunto individual
const AttachmentItem = ({ file, onRemove, showPreview = true }) => {
  const theme = useTheme();
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileType = getFileType(file);

  // Crear URL de vista previa para imágenes
  React.useEffect(() => {
    if (fileType === 'image' && showPreview) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file, fileType, showPreview]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        backgroundColor: theme.palette.action.hover,
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        marginBottom: '8px',
        maxWidth: '100%' // Asegurar que no exceda el ancho del contenedor
      }}
    >
      {/* Vista previa de imagen o icono */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          borderRadius: '6px',
          backgroundColor: theme.palette.background.paper,
          overflow: 'hidden',
          flexShrink: 0
        }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={file.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <Box sx={{ color: theme.palette.primary.main }}>
            {getFileIcon(fileType)}
          </Box>
        )}
      </Box>

      {/* Información del archivo */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          title={file.name} // Mostrar nombre completo en tooltip
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {file.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary }}
        >
          {formatFileSize(file.size)} • {fileType === 'image' ? 'Imagen' : 
           fileType === 'video' ? 'Video' : 
           fileType === 'document' ? 'Documento' : 'Archivo'}
        </Typography>
      </Box>

      {/* Botón para eliminar */}
      <IconButton
        size="small"
        onClick={() => onRemove(file)}
        sx={{
          color: theme.palette.text.secondary,
          '&:hover': {
            color: theme.palette.error.main,
            backgroundColor: theme.palette.error.main + '0a'
          }
        }}
      >
        <X size={16} />
      </IconButton>
    </Box>
  );
};

export function AttachmentUploader({ 
  attachments = [], 
  onAttachmentsChange, 
  maxFiles,
  showPreview = true,
  disabled = false,
  compact = false,
  iconOnly = false
}) {
  const theme = useTheme();
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  const fileInputRef = useRef(null);

  // Manejar selección de archivos
  const handleFileSelection = (files) => {
    setError(null);
    
    const fileArray = Array.from(files);
    const validFiles = [];
    let hasError = false;

    if (maxFiles != null && attachments.length + fileArray.length > maxFiles) {
      setError(`Solo puedes adjuntar hasta ${maxFiles} archivos.`);
      return;
    }

    // Validar cada archivo
    for (const file of fileArray) {
      const validationError = validateAttachmentFile(file);
      if (validationError) {
        setError(validationError);
        hasError = true;
        break;
      }

      // Verificar si el archivo ya está adjunto
      const isDuplicate = attachments.some(existing => 
        existing.name === file.name && existing.size === file.size
      );
      
      if (!isDuplicate) {
        validFiles.push(file);
      }
    }

    if (!hasError && validFiles.length > 0) {
      onAttachmentsChange([...attachments, ...validFiles]);
    }
  };


  // Manejar eliminación de archivo
  const handleRemoveFile = (fileToRemove) => {
    const updatedAttachments = attachments.filter(file => file !== fileToRemove);
    onAttachmentsChange(updatedAttachments);
  };

  // Handler para adjuntar archivos
  const handleFileClick = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  // Input oculto para archivos (siempre necesario)
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept={[...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES, ...ACCEPTED_DOCUMENT_TYPES].join(',')}
      onChange={(e) => handleFileSelection(e.target.files)}
      style={{ display: 'none' }}
    />
  );

  // Modo iconOnly: solo retorna el botón sin contenedor
  if (iconOnly) {
    return (
      <>
        <IconButton
          size="small"
          onClick={handleFileClick}
          disabled={disabled}
          sx={{
            padding: "6px",
            color: attachments.length > 0 
              ? theme.palette.primary.main 
              : theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover
            },
            '&:disabled': {
              color: theme.palette.text.disabled
            }
          }}
          title={attachments.length > 0 
            ? `${attachments.length} archivo(s) adjunto(s)` 
            : "Adjuntar archivo (máx. 50MB)"}
        >
          <Paperclip size={14} />
        </IconButton>
        {fileInput}
      </>
    );
  }

  return (
    <Box>
      {/* Botón de adjuntos */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: compact ? '0' : '12px' }}>
        <IconButton
          size="small"
          onClick={handleFileClick}
          disabled={disabled}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover
            },
            '&:disabled': {
              color: theme.palette.text.disabled
            }
          }}
          title="Adjuntar archivo (máx. 50MB)"
        >
          <Paperclip size={18} />
        </IconButton>
      </Box>

      {/* Input oculto para archivos */}
      {fileInput}

      {/* Lista de archivos adjuntos - solo mostrar si no es compacto */}
      {!compact && attachments.length > 0 && (
        <Stack spacing={1}>
          {attachments.map((file, index) => (
            <AttachmentItem
              key={`${file.name}-${index}`}
              file={file}
              onRemove={handleRemoveFile}
              showPreview={showPreview}
            />
          ))}
        </Stack>
      )}

      {/* Mostrar errores - solo si no es compacto */}
      {!compact && error && (
        <Alert 
          severity="error" 
          sx={{ marginTop: '12px' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Indicador de carga - solo si no es compacto */}
      {!compact && isUploading && (
        <Box sx={{ marginTop: '12px' }}>
          <LinearProgress />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, marginTop: '4px' }}>
            Subiendo archivos...
          </Typography>
        </Box>
      )}
    </Box>
  );
}
